import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import {
  normalizeOperationalSystemRoles,
  OPERATIONAL_SYSTEM_ROLES,
  type OperationalSystemRole,
} from '@/entities/user'
import { useAuth } from '@/features/auth'
import { api, ApiError, queryClient } from '@/shared/api'
import { routes } from '@/shared/config'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'
import { Button, Checkbox } from '@/shared/ui'
import { SuperAdminTabNav } from './super-admin-tab-nav'

type AdminUser = {
  id: string
  email: string
  name?: string | null
  systemRoles?: string[]
  usersCatalogId?: string | null
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
}

type AdminUsersResponse = {
  data: AdminUser[]
  meta: {
    total: number
    page: number
    limit: number
    hasMore: boolean
    search: string | null
    includeDeleted: boolean
  }
}

type FilterState = {
  search: string
  includeDeleted: boolean
}

const defaultFilters: FilterState = {
  search: '',
  includeDeleted: false,
}

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.heading.h2),
}

const subtitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.base),
}

const filterInputStyle = {
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.inputDefault}`,
  borderRadius: designTokens.borders.radius.sm,
  backgroundColor: designTokens.colors.background.surface,
  color: designTokens.colors.text.primary,
  minHeight: designTokens.sizes.height.paymentSummaryPromoInput,
  padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`,
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.sm),
}

const tableHeadStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
}

const cellStyle = {
  borderBottom: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminPattern}`,
  padding: `${designTokens.spacing.sm} ${designTokens.spacing.xs}`,
  verticalAlign: 'top' as const,
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.sm),
  color: designTokens.colors.text.primary,
}

const mutedTextStyle = {
  color: designTokens.colors.text.secondary,
  ...typographyStyle(designTokens.typography.body.xsNormal),
}

const pageSize = 25

const formatDate = (value: string) =>
  new Intl.DateTimeFormat('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value))

const loadAdminUsers = async (filters: FilterState, page: number): Promise<AdminUsersResponse> => {
  const params: Record<string, string> = {
    limit: String(pageSize),
    page: String(page),
    includeDeleted: String(filters.includeDeleted),
  }

  const normalizedSearch = filters.search.trim()
  if (normalizedSearch) {
    params.search = normalizedSearch
  }

  return api.get<AdminUsersResponse>('/api/v1/admin/users', { params })
}

const toRoleSignature = (roles: readonly string[]) => roles.join('|')

const toggleRole = (
  roles: readonly OperationalSystemRole[],
  role: OperationalSystemRole,
  checked: boolean
) =>
  normalizeOperationalSystemRoles(
    checked ? [...roles, role] : roles.filter((currentRole) => currentRole !== role)
  )

export function SuperAdminUsersPage() {
  const { logout, refresh, user } = useAuth()
  const [page, setPage] = useState(1)
  const [draftFilters, setDraftFilters] = useState<FilterState>(defaultFilters)
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(defaultFilters)
  const [roleDrafts, setRoleDrafts] = useState<Record<string, OperationalSystemRole[]>>({})
  const [savingUserId, setSavingUserId] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const usersQuery = useQuery({
    queryKey: ['super-admin-users', page, appliedFilters],
    queryFn: () => loadAdminUsers(appliedFilters, page),
  })

  const users = useMemo(
    () =>
      (usersQuery.data?.data ?? []).map((item) => ({
        ...item,
        normalizedSystemRoles: normalizeOperationalSystemRoles(item.systemRoles),
      })),
    [usersQuery.data]
  )

  const totalPages = Math.max(1, Math.ceil((usersQuery.data?.meta.total ?? 0) / pageSize))

  const applyFilters = () => {
    setPage(1)
    setAppliedFilters({ ...draftFilters, search: draftFilters.search.trim() })
  }

  const resetFilters = () => {
    setPage(1)
    setDraftFilters(defaultFilters)
    setAppliedFilters(defaultFilters)
  }

  const updateRoleDraft = (
    userId: string,
    role: OperationalSystemRole,
    checked: boolean,
    fallbackRoles: readonly OperationalSystemRole[]
  ) => {
    setRoleDrafts((prev) => {
      const currentRoles = prev[userId] ?? [...fallbackRoles]
      return {
        ...prev,
        [userId]: toggleRole(currentRoles, role, checked),
      }
    })
  }

  const handleSaveRoles = async (targetUser: (typeof users)[number]) => {
    const nextRoles = roleDrafts[targetUser.id] ?? targetUser.normalizedSystemRoles

    setSavingUserId(targetUser.id)
    setSaveError(null)

    try {
      await api.patch(`/api/v1/admin/users/${targetUser.id}/system-roles`, {
        systemRoles: nextRoles,
      })

      if (targetUser.id === user?.id) {
        await refresh()
      }

      await queryClient.invalidateQueries({ queryKey: ['super-admin-users'] })

      setRoleDrafts((prev) => ({
        ...prev,
        [targetUser.id]: nextRoles,
      }))
    } catch (error) {
      setSaveError(
        error instanceof ApiError ? `${error.status} ${error.statusText}` : 'Unable to save roles'
      )
    } finally {
      setSavingUserId(null)
    }
  }

  const renderError = () => {
    if (!usersQuery.error && !saveError) {
      return null
    }

    const queryMessage =
      usersQuery.error instanceof ApiError
        ? `${usersQuery.error.status} ${usersQuery.error.statusText}`
        : usersQuery.error
          ? 'Unable to load users'
          : null

    return (
      <div
        style={{
          borderRadius: designTokens.borders.radius.sm,
          backgroundColor: '#fee2e2',
          color: '#7f1d1d',
          padding: designTokens.spacing.md,
          fontFamily: designTokens.typography.fontFamilies.primary,
          ...typographyStyle(designTokens.typography.body.base),
        }}
      >
        {saveError ?? queryMessage}
      </div>
    )
  }

  return (
    <section
      className="mx-auto flex w-full max-w-[1360px] flex-col px-4 py-6 lg:px-8 lg:py-8"
      style={{ gap: designTokens.spacing.lg }}
    >
      <header className="flex w-full flex-col" style={{ gap: designTokens.spacing.sm }}>
        <div
          className="flex flex-wrap items-center justify-between"
          style={{ gap: designTokens.spacing.sm }}
        >
          <Link to={routes.dashboardLk}>
            <Button type="button" variant="secondaryAssist">
              Back To Dashboard
            </Button>
          </Link>
          <Button type="button" variant="secondaryAssist" onClick={logout}>
            Logout
          </Button>
        </div>
        <SuperAdminTabNav activeTab="users" />
        <h1 style={titleStyle}>Super Admin Users</h1>
        <p style={subtitleStyle}>
          Assign operational roles from the existing `users` table. `SUPER_ADMIN` always grants
          access to admin areas even if `ADMIN` is not also stored explicitly.
        </p>
      </header>

      <section
        className="flex flex-col gap-3 rounded-3xl border p-4 lg:flex-row lg:items-end"
        style={{
          borderColor: designTokens.colors.border.adminPattern,
          backgroundColor: designTokens.colors.background.surface,
        }}
      >
        <label className="flex min-w-0 flex-1 flex-col gap-2">
          <span style={tableHeadStyle}>Search</span>
          <input
            value={draftFilters.search}
            onChange={(event) =>
              setDraftFilters((prev) => ({ ...prev, search: event.target.value }))
            }
            placeholder="Email, name, telegram, company, position"
            style={filterInputStyle}
          />
        </label>
        <Checkbox
          checked={draftFilters.includeDeleted}
          onChange={(event) =>
            setDraftFilters((prev) => ({ ...prev, includeDeleted: event.target.checked }))
          }
          label="Include deleted users"
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={applyFilters}>
            Apply
          </Button>
          <Button type="button" variant="secondaryAssist" onClick={resetFilters}>
            Reset
          </Button>
        </div>
      </section>

      {renderError()}

      <section
        className="overflow-hidden rounded-3xl border"
        style={{
          borderColor: designTokens.colors.border.adminPattern,
          backgroundColor: designTokens.colors.background.surface,
        }}
      >
        <div
          className="flex flex-wrap items-center justify-between px-4 py-3"
          style={{ gap: designTokens.spacing.sm }}
        >
          <div style={mutedTextStyle}>
            Total users: {usersQuery.data?.meta.total ?? 0}
            {appliedFilters.search ? ` • Search: ${appliedFilters.search}` : ''}
          </div>
          <div style={mutedTextStyle}>
            Page {page} / {totalPages}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th align="left" style={cellStyle}>
                  <span style={tableHeadStyle}>User</span>
                </th>
                <th align="left" style={cellStyle}>
                  <span style={tableHeadStyle}>Role Access</span>
                </th>
                <th align="left" style={cellStyle}>
                  <span style={tableHeadStyle}>Catalog Link</span>
                </th>
                <th align="left" style={cellStyle}>
                  <span style={tableHeadStyle}>Updated</span>
                </th>
                <th align="left" style={cellStyle}>
                  <span style={tableHeadStyle}>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((targetUser) => {
                const currentDraftRoles =
                  roleDrafts[targetUser.id] ?? targetUser.normalizedSystemRoles
                const hasChanges =
                  toRoleSignature(currentDraftRoles) !==
                  toRoleSignature(targetUser.normalizedSystemRoles)
                const isSaving = savingUserId === targetUser.id

                return (
                  <tr key={targetUser.id}>
                    <td style={cellStyle}>
                      <div className="flex flex-col gap-1">
                        <strong>{targetUser.email}</strong>
                        <span style={mutedTextStyle}>{targetUser.name || 'No name'}</span>
                        {targetUser.deletedAt ? (
                          <span style={{ ...mutedTextStyle, color: '#991b1b' }}>Deleted</span>
                        ) : null}
                      </div>
                    </td>
                    <td style={cellStyle}>
                      <div className="flex min-w-[200px] flex-col gap-2">
                        {OPERATIONAL_SYSTEM_ROLES.map((role) => (
                          <Checkbox
                            key={role}
                            checked={currentDraftRoles.includes(role)}
                            onChange={(event) =>
                              updateRoleDraft(
                                targetUser.id,
                                role,
                                event.target.checked,
                                targetUser.normalizedSystemRoles
                              )
                            }
                            label={role}
                          />
                        ))}
                      </div>
                    </td>
                    <td style={cellStyle}>
                      <div className="flex flex-col gap-1">
                        <span>{targetUser.usersCatalogId ?? 'Not linked'}</span>
                        <span style={mutedTextStyle}>User ID: {targetUser.id}</span>
                      </div>
                    </td>
                    <td style={cellStyle}>
                      <div className="flex flex-col gap-1">
                        <span>Updated: {formatDate(targetUser.updatedAt)}</span>
                        <span style={mutedTextStyle}>
                          Created: {formatDate(targetUser.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td style={cellStyle}>
                      <Button
                        type="button"
                        disabled={!hasChanges || isSaving}
                        onClick={() => {
                          void handleSaveRoles(targetUser)
                        }}
                      >
                        {isSaving ? 'Saving…' : 'Save Roles'}
                      </Button>
                    </td>
                  </tr>
                )
              })}
              {!users.length && !usersQuery.isLoading ? (
                <tr>
                  <td colSpan={5} style={cellStyle}>
                    No users found for the current filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div
          className="flex flex-wrap items-center justify-between border-t px-4 py-3"
          style={{
            gap: designTokens.spacing.sm,
            borderColor: designTokens.colors.border.adminPattern,
          }}
        >
          <span style={mutedTextStyle}>
            {usersQuery.isFetching ? 'Refreshing users…' : 'Role updates are applied immediately.'}
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondaryAssist"
              disabled={page <= 1}
              onClick={() => {
                setPage((prev) => Math.max(1, prev - 1))
              }}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="secondaryAssist"
              disabled={!usersQuery.data?.meta.hasMore}
              onClick={() => {
                setPage((prev) => prev + 1)
              }}
            >
              Next
            </Button>
          </div>
        </div>
      </section>
    </section>
  )
}
